import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FolderKanban, Plus, CircleDashed, Upload, Users, Mic, HardDrive,
  Table as TableIcon, Columns3, CalendarClock,
} from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { EmptyState } from '@/components/ui/States';
import { StatusPill } from '@/components/ui/StatusPill';
import { toast } from '@/components/ui/Toast';
import { runStateTone } from '@/components/runs/RunTranscript';
import { cn } from '@/lib/cn';
import { useIsStaff, useCompanyRole } from '@/app/store';
import { useAsync } from '@/lib/useAsync';
import { timeAgo } from '@/lib/timeAgo';
import { api, ApiError, type ProjectSummary } from '@/lib/api';
import { useWorkspaceCtx } from './WorkspaceLayout';

/** Workspace overview: counts chips + open data gaps + project cards. */
export function WorkspaceOverviewTab() {
  const { company, workspace, reloadWorkspace } = useWorkspaceCtx();
  const isStaff = useIsStaff();
  const [creating, setCreating] = useState(false);

  const chips: [string, number][] = [
    ['Context items', workspace.counts.contextItems],
    ['Knowledge entries', workspace.counts.knowledgeEntries],
    ['Suggestions', workspace.counts.suggestions],
  ];

  return (
    <>
      <div className="mb-6 flex flex-wrap items-center gap-2">
        {chips.map(([label, n]) => (
          <span key={label} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1 text-xs text-fg-muted">
            <span className="font-semibold tabular-nums text-fg">{n}</span> {label}
          </span>
        ))}
        {isStaff && (
          <Button className="ml-auto" onClick={() => setCreating((v) => !v)}>
            <Plus className="h-4 w-4" /> New project
          </Button>
        )}
      </div>

      {creating && (
        <NewProjectForm
          workspaceId={workspace.id}
          base={`/c/${company.slug}/w/${workspace.slug}`}
          onDone={() => { setCreating(false); reloadWorkspace(); }}
        />
      )}

      {isStaff && <ConnectorStrip workspaceId={workspace.id} base={`/c/${company.slug}/w/${workspace.slug}`} />}

      <DataGaps companyId={company.id} workspaceId={workspace.id} base={`/c/${company.slug}/w/${workspace.slug}`} />


      {workspace.projects.length === 0 ? (
        <EmptyState
          icon={FolderKanban}
          title="No projects yet"
          description={isStaff ? 'Create the first workstream for this engagement.' : 'Nothing here yet.'}
        />
      ) : (
        <ProjectsView
          projects={workspace.projects}
          base={`/c/${company.slug}/w/${workspace.slug}`}
          isStaff={isStaff}
          onChanged={reloadWorkspace}
        />
      )}

      <PeopleInvolvedCard workspaceId={workspace.id} companySlug={company.slug} isStaff={isStaff} />
    </>
  );
}

/**
 * The workstream board: a real management view, not cards. Table view is the
 * default (status, agents, schedule, last run, pending, output); Board groups
 * the same rows by status. Staff flip status inline from either view.
 */
function ProjectsView({
  projects,
  base,
  isStaff,
  onChanged,
}: {
  projects: ProjectSummary[];
  base: string;
  isStaff: boolean;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const [view, setView] = useState<'table' | 'board'>('table');
  const [busyId, setBusyId] = useState<string | null>(null);

  async function setStatus(p: ProjectSummary, status: string) {
    setBusyId(p.id);
    try {
      await api.updateProject(p.id, { status: status as 'active' | 'paused' | 'archived' });
      onChanged();
    } catch (err) {
      toast.danger('Could not change status', err instanceof ApiError ? err.message : undefined);
    } finally {
      setBusyId(null);
    }
  }

  const lastRunCell = (p: ProjectSummary) =>
    p.lastRun ? (
      <span className="flex items-center gap-2">
        <Badge tone={runStateTone(p.lastRun.state)}>{p.lastRun.state.toLowerCase()}</Badge>
        <span className="text-xs tabular-nums text-fg-subtle">{timeAgo(p.lastRun.at)}</span>
      </span>
    ) : (
      <span className="text-xs text-fg-subtle">no runs</span>
    );

  const scheduleCell = (p: ProjectSummary) =>
    p.scheduleCadence ? (
      <span className="inline-flex items-center gap-1 text-xs text-fg-muted">
        <CalendarClock className="h-3.5 w-3.5" aria-hidden />
        {p.scheduleCadence.toLowerCase()}
        {p.nextRunAt && <span className="text-fg-subtle">· next {timeAgo(p.nextRunAt)}</span>}
      </span>
    ) : (
      <span className="text-xs text-fg-subtle">manual</span>
    );

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface">
      <div className="flex items-center gap-3 border-b border-border px-4 py-2.5">
        <p className="font-semibold text-fg">Projects</p>
        <div className="ml-auto flex rounded-md border border-border p-0.5">
          {(['table', 'board'] as const).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={cn(
                'flex items-center gap-1.5 rounded-[5px] px-2.5 py-1 text-xs font-medium capitalize transition',
                view === v ? 'bg-heading text-white' : 'text-fg-muted hover:text-fg',
              )}
            >
              {v === 'table' ? <TableIcon className="h-3.5 w-3.5" /> : <Columns3 className="h-3.5 w-3.5" />}
              {v}
            </button>
          ))}
        </div>
      </div>

      {view === 'table' ? (
        <>
          <div className="hidden grid-cols-[2fr_110px_1.2fr_1.3fr_90px_120px] items-center gap-4 border-b border-border px-4 py-2 lg:grid">
            {['Project', 'Status', 'Agents', 'Last run', 'Pending', 'Output'].map((h) => (
              <span key={h} className="text-xs font-medium text-fg-subtle">
                {h}
              </span>
            ))}
          </div>
          <ul className="divide-y divide-border">
            {projects.map((p) => (
              <li
                key={p.id}
                onClick={() => navigate(`${base}/p/${p.slug}`)}
                className="grid cursor-pointer grid-cols-1 items-center gap-2 px-4 py-3 transition hover:bg-surface-2/50 lg:grid-cols-[2fr_110px_1.2fr_1.3fr_90px_120px] lg:gap-4"
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium text-fg">{p.name}</span>
                  {p.goal && <span className="block truncate text-xs text-fg-subtle">{p.goal}</span>}
                </span>
                <span onClick={(e) => e.stopPropagation()}>
                  <StatusPill
                    status={p.status}
                    busy={busyId === p.id}
                    onChange={isStaff ? (s) => setStatus(p, s) : undefined}
                  />
                </span>
                <span className="truncate text-xs text-fg-muted">
                  {p.agentNames.length ? p.agentNames.join(', ') : 'none assigned'}
                </span>
                {lastRunCell(p)}
                <span>
                  {p.pendingSuggestions > 0 ? (
                    <span className="rounded-sm bg-accent px-1.5 py-0.5 text-2xs font-semibold text-white">
                      {p.pendingSuggestions}
                    </span>
                  ) : (
                    <span className="text-xs text-fg-subtle"></span>
                  )}
                </span>
                <span className="text-xs tabular-nums text-fg-muted">
                  {p.deliverableCount} deliverable{p.deliverableCount === 1 ? '' : 's'}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <div className="grid gap-px bg-border sm:grid-cols-3">
          {(['active', 'paused', 'archived'] as const).map((col) => {
            const cards = projects.filter((p) => p.status === col);
            return (
              <div key={col} className="bg-bg p-3">
                <p className="mb-2 flex items-center gap-2 px-1 text-xs font-medium capitalize text-fg-muted">
                  {col}
                  <span className="tabular-nums text-fg-subtle">{cards.length}</span>
                </p>
                <div className="space-y-2">
                  {cards.map((p) => (
                    <div
                      key={p.id}
                      onClick={() => navigate(`${base}/p/${p.slug}`)}
                      className="cursor-pointer rounded-md border border-border bg-surface p-3 transition hover:border-primary/40"
                    >
                      <p className="truncate text-sm font-medium text-fg">{p.name}</p>
                      <div className="mt-2 flex items-center gap-2">
                        {lastRunCell(p)}
                        {p.pendingSuggestions > 0 && (
                          <span className="ml-auto rounded-sm bg-accent px-1.5 py-0.5 text-2xs font-semibold text-white">
                            {p.pendingSuggestions}
                          </span>
                        )}
                      </div>
                      <p className="mt-2 text-xs text-fg-subtle">{scheduleCell(p)}</p>
                    </div>
                  ))}
                  {cards.length === 0 && <p className="px-1 text-xs text-fg-subtle">None</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Slim connector health strip for staff — one glance, links into the tab. */
function ConnectorStrip({ workspaceId, base }: { workspaceId: string; base: string }) {
  const connectors = useAsync(() => api.listConnectors(workspaceId), [workspaceId]);
  if (connectors.status !== 'success' || connectors.data.length === 0) return null;

  return (
    <Link
      to={`${base}/connectors`}
      className="mb-6 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-border bg-surface px-4 py-2.5 transition hover:border-primary/40"
    >
      {connectors.data.map((c) => (
        <span key={c.id} className="flex items-center gap-2 text-xs text-fg-muted">
          {c.kind === 'FIREFLIES' ? <Mic className="h-3.5 w-3.5" aria-hidden /> : <HardDrive className="h-3.5 w-3.5" aria-hidden />}
          <span
            aria-label={c.status.toLowerCase()}
            className={cn(
              'h-2 w-2 rounded-full',
              c.status === 'CONNECTED' ? 'bg-success' : c.status === 'ERROR' ? 'bg-danger' : 'bg-fg-subtle',
            )}
          />
          <span className="font-medium text-fg">{c.name}</span>
          <span className="text-fg-subtle">{c.lastSyncAt ? timeAgo(c.lastSyncAt) : 'never synced'}</span>
        </span>
      ))}
      <span className="ml-auto text-xs font-medium text-primary">Connectors →</span>
    </Link>
  );
}

/** The engagement's cast list — who matters here, from the company register. */
function PeopleInvolvedCard({ workspaceId, companySlug, isStaff }: { workspaceId: string; companySlug: string; isStaff: boolean }) {
  const people = useAsync(() => api.listWorkspacePeople(workspaceId), [workspaceId]);
  if (people.status !== 'success') return null;
  if (people.data.length === 0 && !isStaff) return null;

  return (
    <Card className="mt-6 max-w-xl">
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" aria-hidden /> People involved
          </span>
        }
        action={
          isStaff ? (
            <Link to={`/c/${companySlug}/people`} className="text-sm font-medium text-primary hover:underline">
              Manage
            </Link>
          ) : undefined
        }
      />
      {people.data.length === 0 ? (
        <CardBody className="text-sm text-fg-muted">No one linked yet. Add involvements from the company People tab.</CardBody>
      ) : (
        <ul className="divide-y divide-border">
          {people.data.map((p) => (
            <li key={p.personId} className="flex items-center gap-3 px-5 py-2.5">
              <span className="min-w-0 flex-1">
                <span className="text-sm font-medium text-fg">{p.name}</span>
                {p.title && <span className="ml-2 text-xs text-fg-muted">{p.title}</span>}
              </span>
              {p.role && <Badge tone="neutral">{p.role}</Badge>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * The gap-filling loop, surfaced where the work happens: every PENDING
 * data-gap the agent has flagged, with the resolve/dismiss decision inline
 * (staff + company ADMIN) and a shortcut to add the missing context (staff).
 * Resolve when the material has been added; the next run sees both the new
 * context and the closed gap in its suggestions log.
 */
function DataGaps({ companyId, workspaceId, base }: { companyId: string; workspaceId: string; base: string }) {
  const role = useCompanyRole(companyId);
  const canDecide = role === 'STAFF' || role === 'ADMIN';
  const isStaff = role === 'STAFF';
  const gaps = useAsync(
    async () => (await api.listSuggestions(workspaceId, 'PENDING')).filter((s) => s.kind === 'DATA_GAP'),
    [workspaceId],
  );
  const [busyId, setBusyId] = useState<string | null>(null);

  if (!canDecide || gaps.status !== 'success' || gaps.data.length === 0) return null;

  async function decide(id: string, approve: boolean) {
    setBusyId(id);
    try {
      await (approve ? api.approveSuggestion(id) : api.declineSuggestion(id));
      toast.success(approve ? 'Marked resolved' : 'Dismissed');
      gaps.reload();
    } catch (err) {
      toast.danger('Could not update', err instanceof ApiError ? err.message : undefined);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Card className="mb-6 border-warning/40">
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <CircleDashed className="h-4 w-4 text-warning" /> Open data gaps
            <span className="rounded-sm bg-warning/15 px-1.5 py-0.5 text-2xs font-semibold text-warning">
              {gaps.data.length}
            </span>
          </span>
        }
        subtitle="The agent flagged missing evidence. Add the material, then mark the gap resolved."
        action={
          isStaff ? (
            <Link to={`${base}/context`}>
              <Button variant="secondary" size="sm">
                <Upload className="h-3.5 w-3.5" /> Add context
              </Button>
            </Link>
          ) : undefined
        }
      />
      <ul className="divide-y divide-border">
        {gaps.data.map((g) => (
          <li key={g.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-fg">{g.title}</p>
              <p className="mt-0.5 line-clamp-2 text-xs text-fg-muted">{g.body}</p>
              <p className="kicker mt-1 !text-fg-subtle">{timeAgo(g.createdAt)}</p>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button size="sm" variant="secondary" disabled={busyId === g.id} onClick={() => decide(g.id, true)}>
                Resolved
              </Button>
              <Button size="sm" variant="ghost" disabled={busyId === g.id} onClick={() => decide(g.id, false)}>
                Dismiss
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function NewProjectForm({ workspaceId, base, onDone }: { workspaceId: string; base: string; onDone: () => void }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [goal, setGoal] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const project = await api.createProject(workspaceId, {
        name: name.trim(),
        ...(goal.trim() ? { goal: goal.trim() } : {}),
      });
      toast.success('Project created');
      onDone();
      navigate(`${base}/p/${project.slug}`);
    } catch (err) {
      toast.danger('Could not create project', err instanceof ApiError ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="mb-6">
      <CardBody>
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={submit}>
          <Field label="Project name" htmlFor="project-name">
            <Input id="project-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. July VIP Audit" required autoFocus />
          </Field>
          <Field label="Goal" htmlFor="project-goal" hint="What should this project deliver?">
            <Input id="project-goal" value={goal} onChange={(e) => setGoal(e.target.value)} />
          </Field>
          <div className="sm:col-span-2">
            <Button type="submit" disabled={busy || !name.trim()}>
              {busy ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}
